/**
 * Leviathan Web - Supplier Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SupplierForm from '@components/suppliers/SupplierForm';
import { API_URL } from '@/config/api';
import { fullName } from '@/utils/personName';

interface Receipt {
  id: string;
  receiptNumber: string;
  receiptDate: string;
  status: string;
  total: number;
  items: Array<{
    product: { name: string; sku: string };
  }>;
}

interface Supplier {
  id: string;
  supplierNumber: string;
  name: string;
  displayName: string | null;
  primaryContactFirstName: string | null;
  primaryContactLastName: string | null;
  email: string | null;
  phone: string | null;
  fax: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  taxId: string | null;
  accountNumber: string | null;
  paymentTerms: number | null;
  creditLimit: number | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  receipts: Receipt[];
  _count: {
    receipts: number;
  };
}

const SupplierDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSupplier();
  }, [id]);

  const fetchSupplier = async () => {
    try {
      const response = await fetch(`${API_URL}/api/suppliers/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setSupplier(data);
      } else {
        setError('Supplier not found');
      }
    } catch (error) {
      console.error('Failed to fetch supplier:', error);
      setError('Failed to load supplier');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!supplier) return;
    if (!confirm('Are you sure you want to deactivate this supplier?')) return;

    try {
      const response = await fetch(`${API_URL}/api/suppliers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        navigate('/suppliers');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to deactivate supplier');
      }
    } catch (error) {
      console.error('Failed to deactivate supplier:', error);
      setError('Failed to deactivate supplier');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'PENDING':
        return 'text-warning-amber';
      case 'RECEIVED':
        return 'text-steel';
      case 'VERIFIED':
        return 'text-success-green';
      case 'POSTED':
        return 'text-success-green';
      case 'DISPUTED':
        return 'text-error-red';
      case 'CANCELLED':
        return 'text-text-tertiary';
      default:
        return 'text-text-secondary';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading supplier...</div>
      </div>
    );
  }

  if (error && !supplier) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <div className="text-error-red font-mono mb-4">&gt; {error}</div>
        <Link to="/suppliers" className="text-steel hover:text-amber font-mono transition-colors">
          &larr; Back to Suppliers
        </Link>
      </div>
    );
  }

  if (!supplier) return null;

  if (isEditing) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
          <div className="border-l-4 border-amber pl-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <h1
                  className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                >
                  Edit Supplier
                </h1>
                <div className="flex items-center space-x-4 mt-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
                  >
                    &larr; Cancel Editing
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <SupplierForm
          supplierId={supplier.id}
          initialData={{
            name: supplier.name,
            displayName: supplier.displayName || '',
            primaryContactFirstName: supplier.primaryContactFirstName || '',
            primaryContactLastName: supplier.primaryContactLastName || '',
            email: supplier.email || '',
            phone: supplier.phone || '',
            fax: supplier.fax || '',
            website: supplier.website || '',
            address: supplier.address || '',
            city: supplier.city || '',
            state: supplier.state || '',
            zip: supplier.zip || '',
            taxId: supplier.taxId || '',
            accountNumber: supplier.accountNumber || '',
            paymentTerms: supplier.paymentTerms?.toString() || '30',
            creditLimit: supplier.creditLimit?.toString() || '',
            notes: supplier.notes || '',
            isActive: supplier.isActive,
          }}
          onSuccess={() => {
            setIsEditing(false);
            fetchSupplier();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4">
                <h1
                  className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                >
                  {supplier.name}
                </h1>
                <span
                  className={`px-3 py-1 font-mono text-sm uppercase tracking-wider border ${supplier.isActive ? 'text-success-green bg-success-green/20 border-success-green/30' : 'text-text-tertiary bg-text-tertiary/20 border-text-tertiary/30'}`}
                >
                  {supplier.isActive ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <span className="text-text-secondary text-sm font-mono">
                  {supplier.supplierNumber}
                </span>
                <Link
                  to="/suppliers"
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
                >
                  &larr; Back to Suppliers
                </Link>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-2 bg-steel/20 hover:bg-steel/30 text-steel font-mono text-sm uppercase tracking-wider transition-all border border-steel/30"
              >
                Edit
              </button>
              {supplier.isActive && (
                <button
                  onClick={handleDeactivate}
                  className="px-4 py-2 bg-error-red/20 hover:bg-error-red/30 text-error-red font-mono text-sm uppercase tracking-wider transition-all border border-error-red/30"
                >
                  Deactivate
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Supplier Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Info */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Contact Information
              </h3>
            </div>
            <div className="space-y-3 text-sm">
              {fullName({ firstName: supplier.primaryContactFirstName, lastName: supplier.primaryContactLastName }) && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">
                    Primary Contact
                  </div>
                  <div className="text-text-primary font-mono">
                    {fullName({
                      firstName: supplier.primaryContactFirstName,
                      lastName: supplier.primaryContactLastName,
                    })}
                  </div>
                </div>
              )}
              {supplier.phone && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Phone</div>
                  <div className="text-text-primary font-mono">{supplier.phone}</div>
                </div>
              )}
              {supplier.email && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Email</div>
                  <div className="text-text-primary font-mono">{supplier.email}</div>
                </div>
              )}
              {supplier.fax && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Fax</div>
                  <div className="text-text-primary font-mono">{supplier.fax}</div>
                </div>
              )}
              {supplier.website && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Website</div>
                  <div className="text-text-primary font-mono">{supplier.website}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Address
              </h3>
            </div>
            <div className="space-y-1">
              {supplier.address && (
                <div className="text-text-primary font-mono text-sm">{supplier.address}</div>
              )}
              {(supplier.city || supplier.state || supplier.zip) && (
                <div className="text-text-secondary font-mono text-sm">
                  {[supplier.city, supplier.state].filter(Boolean).join(', ')}{' '}
                  {supplier.zip}
                </div>
              )}
              {!supplier.address && !supplier.city && (
                <div className="text-text-tertiary font-mono text-sm">No address on file</div>
              )}
            </div>
          </div>
        </div>

        {/* Business Info */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Business Details
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {supplier.accountNumber && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Account #</div>
                  <div className="text-text-primary font-mono">{supplier.accountNumber}</div>
                </div>
              )}
              {supplier.taxId && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Tax ID</div>
                  <div className="text-text-primary font-mono">{supplier.taxId}</div>
                </div>
              )}
              <div>
                <div className="text-text-tertiary font-mono uppercase text-xs">Payment Terms</div>
                <div className="text-text-primary font-mono">
                  {supplier.paymentTerms ? `NET ${supplier.paymentTerms}` : 'COD'}
                </div>
              </div>
              <div>
                <div className="text-text-tertiary font-mono uppercase text-xs">Credit Limit</div>
                <div
                  className="text-amber font-mono font-semibold"
                  style={{ textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' }}
                >
                  {supplier.creditLimit ? formatCurrency(supplier.creditLimit) : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Statistics
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-text-tertiary font-mono uppercase text-xs">Total Receipts</div>
                <div
                  className="text-2xl text-amber font-mono font-bold"
                  style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.6)' }}
                >
                  {supplier._count.receipts}
                </div>
              </div>
              <div>
                <div className="text-text-tertiary font-mono uppercase text-xs">Member Since</div>
                <div className="text-text-primary font-mono">{formatDate(supplier.createdAt)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Receipts */}
      {supplier.receipts.length > 0 && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
            <div className="p-6 border-b border-panel-border">
              <div className="flex items-center space-x-2">
                <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
                <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                  Recent Receipts
                </h3>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Receipt #
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Date
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Products
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Total
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {supplier.receipts.map((receipt) => (
                    <tr key={receipt.id} className="border-b border-panel-border">
                      <td className="px-6 py-4">
                        <Link
                          to={`/receipts/${receipt.id}`}
                          className="text-steel hover:text-amber font-mono font-semibold transition-colors"
                        >
                          {receipt.receiptNumber}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-text-secondary font-mono text-sm">
                        {formatDate(receipt.receiptDate)}
                      </td>
                      <td className="px-6 py-4 text-text-secondary font-mono text-sm">
                        {receipt.items.map((item) => item.product.name).join(', ')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span
                          className="text-amber font-mono font-semibold"
                          style={{ textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' }}
                        >
                          {formatCurrency(receipt.total)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`${getStatusColor(receipt.status)} font-mono text-xs uppercase tracking-wider font-semibold`}
                          style={{ textShadow: '0 0 6px currentColor' }}
                        >
                          {receipt.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {supplier.notes && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Notes
              </h3>
            </div>
            <p className="text-text-secondary font-mono text-sm whitespace-pre-wrap">
              {supplier.notes}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierDetailPage;
